import { Box, Link, TextField, Typography } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const SAVE_AFTER_MS = 1000;

const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '');

/**
 * A box that saves about a second after you stop typing, and says Saving, Saved or Couldn't save.
 * onSave(value, rev) saves and resolves with the box's new rev. Only one save runs at a time, so
 * a save never races its own rev. If someone else changed the box the save is held, and the box
 * shows their text with Use theirs and Keep mine. flush() waits for the last save.
 */
const AutosaveText = forwardRef(function AutosaveText(
    { label, initial, rev, onSave, onReload, minRows = 3, multiline = true, inputSx, shortName, placeholder },
    ref,
) {
    const [text, setText] = useState(initial || '');
    const [state, setState] = useState('saved');
    const [problem, setProblem] = useState(null);
    const [conflict, setConflict] = useState(null);
    const timer = useRef(null);
    const seenRev = useRef(rev || 0);
    const latest = useRef(initial || '');
    const running = useRef(null);
    const stateRef = useRef('saved');
    const held = useRef(false);

    const setSaveState = (s) => {
        stateRef.current = s;
        setState(s);
    };

    const save = async () => {
        clearTimeout(timer.current);
        timer.current = null;
        // What was typed meanwhile goes when this one finishes
        if (running.current) return running.current;
        const value = latest.current;
        setSaveState('saving');
        setProblem(null);
        const attempt = (async () => {
            try {
                const box = await onSave(value, seenRev.current);
                seenRev.current = box.rev;
                if (latest.current === value) {
                    setSaveState('saved');
                }
                return true;
            } catch (e) {
                const data = e?.response?.data;
                const status = e?.response?.status;
                if (data?.code === 'TEXT_CHANGED') {
                    held.current = true;
                    setConflict({ by: data.changedBy, at: data.changedAt, value: data.value, rev: data.rev });
                } else if (status === 400 || status === 403 || status === 409) {
                    setProblem(data?.message);
                }
                // A retry can't get past a 403 or a 409: the stage has moved on
                setSaveState(status === 403 || (status === 409 && data?.code !== 'TEXT_CHANGED') ? 'blocked' : 'error');
                return false;
            }
        })();
        running.current = attempt;
        const ok = await attempt;
        running.current = null;
        // Typed again while that was saving
        if (ok && latest.current !== value) {
            return save();
        }
        return ok;
    };

    useImperativeHandle(ref, () => ({
        flush: async () => {
            while (running.current) await running.current;
            if (held.current) return false;
            return timer.current || stateRef.current !== 'saved' ? save() : true;
        },
        value: () => latest.current,
        unsaved: () => stateRef.current !== 'saved',
    }));

    useEffect(() => () => clearTimeout(timer.current), []);

    useEffect(() => {
        const warn = (e) => {
            if (stateRef.current !== 'saved' && stateRef.current !== 'blocked') {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, []);

    const change = (value) => {
        setText(value);
        latest.current = value;
        setSaveState('saving');
        clearTimeout(timer.current);
        timer.current = setTimeout(save, SAVE_AFTER_MS);
    };

    const keepMine = () => {
        seenRev.current = conflict.rev;
        held.current = false;
        setConflict(null);
        save();
    };

    return (
        <>
            <TextField
                fullWidth
                multiline={multiline}
                minRows={multiline ? minRows : undefined}
                label={label}
                placeholder={placeholder}
                value={text}
                disabled={!!conflict}
                onChange={(e) => change(e.target.value)}
                InputProps={{ sx: inputSx }}
            />
            <Typography
                variant="caption"
                color={state === 'error' || state === 'blocked' ? 'error' : 'text.secondary'}
                component="div"
                aria-live="polite"
            >
                {conflict ? (
                    <>
                        {shortName ? shortName(conflict.by) : conflict.by} changed this at {timeOf(conflict.at)}. Their text:
                        <Box sx={{ my: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {conflict.value || 'Empty'}
                        </Box>
                        <Link component="button" variant="caption" onClick={onReload}>
                            Use theirs
                        </Link>{' '}
                        ·{' '}
                        <Link component="button" variant="caption" onClick={keepMine}>
                            Keep mine
                        </Link>
                    </>
                ) : state === 'saving' ? (
                    'Saving'
                ) : state === 'blocked' ? (
                    `${problem || "You can't change this now."} This wasn't saved.`
                ) : state === 'error' ? (
                    <>
                        {problem || "Couldn't save"} ·{' '}
                        <Link component="button" variant="caption" onClick={save}>
                            Retry
                        </Link>
                    </>
                ) : (
                    'Saved'
                )}
            </Typography>
        </>
    );
});

export default AutosaveText;
